import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { tagKeys } from "../keys/tags";
import { productKeys } from "../keys/products";
import type {
  TagResponse,
  TagCreateRequest,
  TagUpdateRequest,
  TagReorderRequest,
  BatchTagsRequest,
} from "@/app/api/types";
import { UUID } from "crypto";

/**
 * Hook to get all tags for the current company
 */
export function useTags() {
  return useQuery({
    queryKey: tagKeys.list(),
    queryFn: async () => {
      return await api.tags.list();
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get a specific tag by ID
 */
export function useTag(tagId: UUID | undefined) {
  return useQuery({
    queryKey: tagKeys.detail(tagId as string),
    queryFn: async () => {
      if (!tagId) throw new Error("Tag ID is required");
      return await api.tags.get(tagId);
    },
    enabled: !!tagId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to get all tags for a specific product
 */
export function useProductTags(productId: UUID | undefined) {
  return useQuery({
    queryKey: tagKeys.productTags(productId as string),
    queryFn: async () => {
      if (!productId) throw new Error("Product ID is required");
      return await api.tags.getProductTags(productId);
    },
    enabled: !!productId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TagCreateRequest) => {
      return await api.tags.create(data);
    },
    onSuccess: (newTag) => {
      // Optimistically update the cache with the new tag
      queryClient.setQueryData<TagResponse[]>(tagKeys.list(), (old) => {
        if (!old) return [newTag];
        return [...old, newTag];
      });

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Hook to update an existing tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: UUID; data: TagUpdateRequest }) => {
      return await api.tags.update(id, data);
    },
    onSuccess: (updatedTag) => {
      // Optimistically update the cache
      queryClient.setQueryData<TagResponse[]>(tagKeys.list(), (old) => {
        if (!old) return [updatedTag];
        return old.map((tag) => (tag.id === updatedTag.id ? updatedTag : tag));
      });

      // Update the detail cache
      queryClient.setQueryData(
        tagKeys.detail(updatedTag.id as unknown as string),
        updatedTag,
      );

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Hook to delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: UUID) => {
      return await api.tags.delete(id);
    },
    onSuccess: (_, deletedId) => {
      // Optimistically remove from cache
      queryClient.setQueryData<TagResponse[]>(tagKeys.list(), (old) => {
        if (!old) return [];
        return old.filter((tag) => tag.id !== deletedId);
      });

      // Also invalidate to ensure sync with server
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      // Invalidate products since their tags may have changed
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Hook to reorder tags (update priorities)
 */
export function useReorderTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TagReorderRequest) => {
      return await api.tags.reorder(data);
    },
    onMutate: async (data) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: tagKeys.list() });

      // Snapshot the previous value
      const previousTags = queryClient.getQueryData<TagResponse[]>(
        tagKeys.list(),
      );

      // Optimistically reorder the tags
      if (previousTags) {
        const tagMap = new Map(previousTags.map((tag) => [tag.id, tag]));
        const reorderedTags = data.tag_ids
          .map((id, index) => {
            const tag = tagMap.get(id);
            if (tag) {
              return { ...tag, priority: index };
            }
            return null;
          })
          .filter((t): t is TagResponse => t !== null);

        queryClient.setQueryData<TagResponse[]>(tagKeys.list(), reorderedTags);
      }

      return { previousTags };
    },
    onError: (_err, _data, context) => {
      // Rollback on error
      if (context?.previousTags) {
        queryClient.setQueryData(tagKeys.list(), context.previousTags);
      }
    },
    onSettled: () => {
      // Always refetch after mutation
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Hook to sync (replace) all tags for a specific product
 */
export function useSyncProductTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      tagIds,
    }: {
      productId: UUID;
      tagIds: UUID[];
    }) => {
      return await api.tags.syncProductTags(productId, { tag_ids: tagIds });
    },
    onSuccess: (newTags, { productId }) => {
      // Update the product tags cache
      queryClient.setQueryData(
        tagKeys.productTags(productId as unknown as string),
        newTags,
      );

      // Invalidate product queries to reflect tag changes
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Hook to batch add tags to multiple products
 */
export function useBatchAddTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BatchTagsRequest) => {
      return await api.tags.batchAddTags(data);
    },
    onSuccess: () => {
      // Invalidate all product and tag queries
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Hook to batch remove tags from multiple products
 */
export function useBatchRemoveTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BatchTagsRequest) => {
      return await api.tags.batchRemoveTags(data);
    },
    onSuccess: () => {
      // Invalidate all product and tag queries
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

/**
 * Hook to batch replace tags on multiple products
 */
export function useBatchReplaceTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BatchTagsRequest) => {
      return await api.tags.batchReplaceTags(data);
    },
    onSuccess: () => {
      // Invalidate all product and tag queries
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}
